import Project from '@/models/Project'
import Paginator from '@/models/Paginator'
import { project as backend } from '@/api'
import { common as pathLoader } from '@/api'
import { findIndex } from 'lodash'

export default {

  namespaced: true,

  state: {
    project: new Project,
    projects: new Paginator
  },

  mutations: {
    clear: (state) => {
      state.project = new Project
      state.projects = new Paginator
    },
    clearProject: (state) => state.project = new Project,
    setProject: (state, payload) => state.project = payload,
    setProjects: (state, payload) => state.projects = payload,
    name: (state, payload) => state.project.name = payload,
    startDate: (state, payload) => state.project.start = payload,
    endDate: (state, payload) => state.project.end = payload,
    brief: (state, payload) => state.project.brief = payload,
    clientUser: (state, payload) => state.project.client_user_id = payload,
    connection: (state, payload) => state.project.connection_id = payload,
    updateType: (state, payload) => state.project.type = payload,
    addCollaborator: (state, payload) => state.project.collaborators.push(payload),
    removeCollaborator: (state, payload) => {
      let index = state.project.collaborators.indexOf(payload)
      state.project.collaborators.splice(index, 1)
    },
    removeTaskFromMilestone: (state, payload) => {
      let milestoneIndex = findIndex(state.project.milestones, milestone => milestone.id === payload.milestone.id)
      let milestone = state.project.milestones[milestoneIndex]
      milestone.tasks = milestone.tasks.filter(task => task.id !== payload.task.id)
      state.project.milestones.splice(milestoneIndex, 1, milestone)
    },
    sortMilestones: (state, payload) => {
      state.project.milestones = payload.map((milestone, index) => {
        milestone.sort = index
        return milestone
      })
    },
    updateMilestoneTitle: (state, payload) => {
      let milestoneIndex = findIndex(state.project.milestones, milestone => milestone.id === payload.milestoneId)
      let milestone = state.project.milestones[milestoneIndex]
      milestone.title = payload.title
      state.project.milestones.splice(milestoneIndex, 1, milestone)
    },
    updateMilestoneDueDate: (state, payload) => {
      let milestoneIndex = findIndex(state.project.milestones, milestone => milestone.id === payload.milestoneId)
      let milestone = state.project.milestones[milestoneIndex]
      milestone.due_date = payload.due_date
      state.project.milestones.splice(milestoneIndex, 1, milestone)
    },
    updateStatus: (state, payload) => state.project.status = payload,
    addBriefAmendment: (state, payload) => state.project.brief_amendments.push(payload),
  },

  actions: {
    save: ({ state }) => new Promise((resolve, reject) => {
      if (state.project.id) {
        backend.save(state.project, ({ data }) => resolve(data)).catch(error => reject(error))
      } else {
        backend.store(state.project, ({ data }) => resolve(data)).catch(error => reject(error))
      }
    }),
    storeRequest: ({ state }) => new Promise((resolve, reject) => {
      backend.storeRequest(state.project, ({ data }) => resolve(data)).catch(error => reject(error))
    }),
    loadProjects: ({ commit }, payload) => new Promise((resolve, reject) => {
      pathLoader.loadPath(payload, ({ data }) => {
        commit('setProjects', data)
        resolve()
      }, error => reject(error))
    }),
    loadClientProjects: ({ commit }, payload) => new Promise((resolve, reject) => {
      pathLoader.loadPath(payload, ({ data }) => {
        commit('setProjects', data)
        resolve()
      }, error => reject(error))
    }),
    loadProject: ({ commit }, projectId) => new Promise((resolve, reject) => {
      backend.loadProject(projectId, ({ data }) => {
        commit('setProject', data)
        resolve(data)
      }, error => reject(error))
    }),
    loadProjectManager: ({ commit }, path) => new Promise((resolve, reject) => {
      pathLoader.loadPath(path, ({ data }) => {
        commit('setProject', data.project)
        resolve(data.tasks)
      }, error => reject(error))
    }),
    close: ({ state }) => new Promise((resolve, reject) => {
      backend.close(state.project.id, response => resolve(response), error => reject(error))
    }),
    complete: ({ state }) => new Promise((resolve, reject) => {
      backend.complete(state.project.id, response => resolve(response), error => reject(error))
    }),
    open: ({ state }) => new Promise((resolve, reject) => {
      backend.open(state.project.id, response => resolve(response), error => reject(error))
    }),
    deleteProject: ({ state }) => new Promise((resolve, reject) => {
      backend.deleteProject(state.project.id, response => resolve(response), error => reject(error))
    }),
    addCollaborator: ({ state, commit }, company) => new Promise((resolve, reject) => {
      backend.addCollaborator({
        project: state.project.id,
        company
      }, ({ data }) => {
        commit('addCollaborator', data)
        resolve()
      }, error => reject(error))
    }),
    removeCollaborator: ({ state, commit }, company) => new Promise((resolve, reject) => {
      backend.removeCollaborator({
        project: state.project.id,
        company
      }, () => {
        commit('removeCollaborator', company)
        resolve()
      }, error => reject(error))
    }),
    sortMilestones: ({ state }, milestones) => new Promise((resolve, reject) => {
      backend.sortMilestones({
        project: state.project.id,
        milestones: milestones.map(mile => mile.id)
      }, () => {
        resolve()
      }, error => reject(error))
    }),
    startReview: ({ state, commit }) => new Promise((resolve, reject) => {
      backend.startReview(state.project.id, ({data}) => {
        commit('updateStatus', data)
        resolve()
      }, error => reject(error))
    }),
    acceptRequest: ({ state, commit }) => new Promise((resolve, reject) => {
      backend.acceptRequest(state.project.id, ({data}) => {
        commit('updateStatus', data)
        resolve()
      }, error => reject(error))
    }),
    declineRequest: ({ state, commit }, reason) => new Promise((resolve, reject) => {
      backend.declineRequest({
        projectId: state.project.id, 
        reason
      }, ({data}) => {
        commit('updateStatus', data)
        resolve()
      }, error => reject(error))
    }),
    submitBriefAmendment: ({ state, commit }, content) => new Promise((resolve, reject) => {
      backend.submitBriefAmendment({
        projectId: state.project.id,
        content
      }, ({data}) => {
        commit('addBriefAmendment', data)
        resolve()
      }, error => reject(error))
    })
  },

  getters: {
    projects: state => state.projects,
    project: state => state.project,
    milestones: state => state.project.milestones,
    tasks: state => state.project.tasks,
    openTasks: ({}, getters) => {
      return getters.tasks.filter(task => !task.completed_at)
    },
    closedTasks: ({}, getters) => {
      return getters.tasks.filter(task => task.completed_at)
    }
  }

}
